import MComponent from '../Toolkit/Components/Component';
import {withMsal, WithMsalProps} from '@azure/msal-react';
import WithRouterProps from '../Toolkit/WithRouterProps';
import MPage, {MPageProps} from '../Toolkit/Components/Shell/Page';
import MTable, {MTableProps} from '../Toolkit/Components/Controls/Table';
import NSwagAdapter from '../Toolkit/Api/NSwagAdapter';
import MNSwagClientConfiguration from '../Toolkit/Api/NSwagClientConfiguration';
import SafeDigAccessTokenProvider from '../Api/SafeDigAccessTokenProvider';
import React, {createRef, ReactElement, RefObject} from 'react';
import WithRouter from '../Toolkit/WithRouter';
import MToolbar from '../Toolkit/Components/Controls/Toolbar';
import MButton from '../Toolkit/Components/Controls/Button';
import {MOpenIconName} from '../Toolkit/Icons';
import {DataTable} from 'primereact/datatable';
import {FilterMatchMode} from 'primereact/api';
import {Column} from 'primereact/column';
import EnumContext from '../Context/enum-context';
import {Calendar} from 'primereact/calendar';
import {RegistrationListItem, RegistrationListReply} from '../Api/RegistrationTypes';
import {RegistrationClient} from '../Api/RegistrationClients';
import MSpacer from '../Toolkit/Components/Controls/Spacer';
import {GotoIconName} from './SafeDigConstants';
import {MDropDownItem} from '../Toolkit/Components/Controls/DropDownOption';
import MMultiDropDown from '../Toolkit/Components/Controls/MultiDropDown';

type RegistrationRow = {
    registration: RegistrationListItem;
}

type Props = WithMsalProps & WithRouterProps & {};

/** */
type State = {
    page: MPageProps;
    table: MTableProps<RegistrationRow>;
    selectedRow?: RegistrationRow;
}

class ListRegistrationsPage extends MComponent<Props, State> {
    static contextType = EnumContext;
    context!: React.ContextType<typeof EnumContext>;

    private readonly m_registrationClient: NSwagAdapter<RegistrationClient>;
    private readonly m_datatableRef: RefObject<DataTable>;

    public constructor(props: Props) {
        super(props);

        this.bindMethods();

        this.m_registrationClient = new NSwagAdapter<RegistrationClient>(new RegistrationClient(
            new MNSwagClientConfiguration(process.env.REACT_APP_SAFEDIGS_API_KEY!,
                new SafeDigAccessTokenProvider(this.props.msalContext))));
        this.m_datatableRef = createRef<DataTable>();

        this.initializeState();
    }

    private bindMethods(): void {
        this.open_Click = this.open_Click.bind(this);
        this.notices_Click = this.notices_Click.bind(this);
        this.table_onSelectionChange = this.table_onSelectionChange.bind(this);
        this.page_onLoadData = this.page_onLoadData.bind(this);
        this.isOpenButtonDisabled = this.isOpenButtonDisabled.bind(this);
        this.getToolbarTemplate = this.getToolbarTemplate.bind(this);
    }

    private initializeState(): void {
//  Page
        const page: MPageProps = {
            locked: false
        };

        //  Table
        const table: MTableProps<RegistrationRow> = {
            exportName: 'Registrations',
            rowData: [],
            filters: {
                'registration.noticeHeader.odinId': {
                    value: null,
                    matchMode: FilterMatchMode.CONTAINS
                },
                'registration.registration.digNumber': {
                    value: null,
                    matchMode: FilterMatchMode.EQUALS
                },
                'registration.noticeDetail.permitNumber': {
                    value: null,
                    matchMode: FilterMatchMode.CONTAINS
                },
                'registration.registration.regStatus': {
                    value: null,
                    matchMode: FilterMatchMode.IN
                },
                'registration.registration.reinstatementDate': {
                    value: null,
                    matchMode: FilterMatchMode.DATE_IS
                },
                'registration.registration.digLocation': {
                    value: null,
                    matchMode: FilterMatchMode.CONTAINS
                },
                'registration.registration.tempPerm': {
                    value: null,
                    matchMode: FilterMatchMode.IN
                },
                'registration.registration.createdOn': {
                    value: null,
                    matchMode: FilterMatchMode.DATE_IS
                },
                'registration.registration.createdBy': {
                    value: null,
                    matchMode: FilterMatchMode.CONTAINS
                }
            }
        };

        this.state = {
            page: page,
            table: table,
            selectedRow: undefined
        };
    }

    private isOpenButtonDisabled(): boolean {
        return this.state.selectedRow === undefined;
    }

    private open_Click(): void {
        this.props.navigate(`/registrations/${this.state.selectedRow!.registration.registration!.id}`);
    }

    private notices_Click(): void {
        this.props.navigate('/notices');
    }

    private table_onSelectionChange(selection: RegistrationRow | RegistrationRow[] | undefined): void {

        this.safeSetState((draft: State) =>
            draft.selectedRow = selection as RegistrationRow | undefined);

    }

    private async page_onLoadData(): Promise<void> {
        const listResult = await this.m_registrationClient.callWithReply(
            RegistrationListReply, (client) => client.listAllRegistrations()
        );

        listResult.throwIfFailed();

        this.safeSetState((draft: State) => {
            draft.table.rowData = listResult.reply!.registrations
                                                   .map((w: RegistrationListItem) => {
                                                       return {registration: w} as RegistrationRow;
                                                   });
        });
    }

    private getToolbarTemplate(): ReactElement<MToolbar> {
        return (
            <MToolbar>
                <MButton label="Open"
                         icon={MOpenIconName}
                         iconOnly={false}
                         disabled={this.isOpenButtonDisabled}
                         onClick={this.open_Click}/>
                <MSpacer/>
                <MButton label="Notices"
                         color="help"
                         icon={GotoIconName}
                         iconOnly={false}
                         disabled={false}
                         onClick={this.notices_Click}/>
            </MToolbar>
        );
    }

    private noticeTemplate = (rowData: RegistrationRow): ReactElement => {
        return <a href={`/notices/${rowData.registration.noticeHeader!.id}`} >
            {rowData.registration.noticeHeader!.odinId}
        </a>;
    }

    private statusTemplate = (rowData: RegistrationRow) : string => {
        const index = this.context.enums?.regStatuses.findIndex(value => value.id === rowData.registration.registration!.regStatus);

        if(index === undefined || index === -1) {
            return '';
        }

        return this.context.enums?.regStatuses[index!].name ?? '';
    }

    private statusFilterTemplate = (options: any) => {
        if(this.context.enums === null) {
            return <div></div>;
        }

        const itemList: MDropDownItem<number>[] = (
            this.context.enums!.regStatuses
                               .map((enumValue) => {
                                   return {label: enumValue.name, value: enumValue.id};
                               })
        );

        return (
            <MMultiDropDown id="statusFilter"
                            items={itemList}
                            disabled={false}
                            onChange={(selection: number[]) => options.filterApplyCallback(selection)}/>
        );
    }

    private createdDateTemplate = (rowData: RegistrationRow): string => {
        if(rowData.registration.registration?.createdOn != null) {
            try {
                return rowData.registration.registration!.createdOn.toLocaleDateString();
            } catch {
                return '';
            }
        }
        return '';
    };

    private reinstatementDateTemplate = (rowData: RegistrationRow): string => {
        if(rowData.registration.registration?.reinstatementDate != null) {
            try {
                return rowData.registration.registration!.reinstatementDate?.toLocaleDateString() ?? "";
            } catch {
                return '';
            }
        }
        return '';
    };

    private dateFilterTemplate = (options: any) => {
        return <Calendar value={options.value}
                         onChange={(e: any) => options.filterApplyCallback(e.value, options.index)}
                         dateFormat="dd/mm/yy"
                         placeholder="dd/mm/yyyy"
                         mask="99/99/9999"/>;
    };

    private tempPermTemplate = (rowData: RegistrationRow) => {
        if(rowData.registration.registration?.tempPerm) {
            return 'Permanent';
        }
        return 'Temporary';
    }

    private tempPermFilterTemplate = (options: any) => {
        const itemList: MDropDownItem<boolean>[] = [
            {label: 'Temporary', value: false},
            {label: 'Permanent', value: true}
        ];

        return (
            <MMultiDropDown id="tempPermFilter"
                            items={itemList}
                            disabled={false}
                            onChange={(selection: boolean[]) => options.filterApplyCallback(selection)}/>
        );
    }

    public render(): ReactElement {
        return (
            <MPage title="Registrations"
                   locked={this.state.page.locked}
                   toolbar={this.getToolbarTemplate()}
                   onLoadData={this.page_onLoadData}>

                <MTable innerRef={this.m_datatableRef}
                        rowKey="registration.registration.id"
                        rowData={this.state.table.rowData}
                        filters={this.state.table.filters}
                        onSelectionChange={this.table_onSelectionChange}
                        sortMode={'multiple'}
                        multiSortMeta={[{field: 'registration.registration.createdOn', order: -1}]}>

                    <Column field="registration.noticeHeader.odinId"
                            header="Notice"
                            filter={true}
                            sortable={true}
                            body={this.noticeTemplate}
                            style={{width: '10%'}}/>
                    <Column field="registration.registration.digNumber"
                            header="Dig Number"
                            filter={true}
                            sortable={true}
                            style={{width: '10%'}}/>
                    <Column field="registration.noticeDetail.permitNumber"
                            header="Permit"
                            filter={true}
                            sortable={true}
                            style={{width: '10%'}}/>
                    <Column field="registration.registration.regStatus"
                            header="Status"
                            filter={true}
                            filterElement={this.statusFilterTemplate}
                            body={this.statusTemplate}
                            sortable={true}
                            style={{width: '10%'}}/>
                    <Column field="registration.registration.reinstatementDate"
                            header="Reinstatement Date"
                            dataType="date"
                            filter={true}
                            filterElement={this.dateFilterTemplate}
                            sortable={true}
                            body={this.reinstatementDateTemplate}
                            style={{width: '15%'}}/>
                    <Column field="registration.registration.tempPerm"
                            header="Temp/Perm"
                            filter={true}
                            filterElement={this.tempPermFilterTemplate}
                            sortable={true}
                            body={this.tempPermTemplate}
                            style={{width: '10%'}}/>
                    <Column field="registration.registration.digLocation"
                            header="Dig Location"
                            filter={true}
                            sortable={true}
                            style={{width: '34%'}}/>
                    <Column field="registration.registration.createdBy"
                            header="Created By"
                            filter={true}
                            sortable={true}
                            style={{width: '18%'}}/>
                    <Column field="registration.registration.createdOn"
                            header="Created"
                            dataType="date"
                            filter={true}
                            filterElement={this.dateFilterTemplate}
                            sortable={true}
                            body={this.createdDateTemplate}
                            style={{width: '15%'}}/>
                </MTable>
            </MPage>
        );
    }
}

export default withMsal(WithRouter(ListRegistrationsPage));


